import type { Task } from '@lmc/ts-common'
import { Heading, Modal, theme, Warning } from '@lmc/ui-kit'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Form } from 'antd'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { toDosConstants } from 'locale/toDos'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import styled from 'styled-components'
import type { ActionModalProps } from '..'
import CommentSection, { ITEM_NAME } from '../components/CommentSection'
import ModalDescription from '../components/ModalDescription'

const SnoozeModal = ({
  task,
  isModalVisible,
  setIsModalVisible,
  onSuccess
}: ActionModalProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const mutationFunction = ({
    comment
  }: {
    comment?: string
  }): Promise<{
    result: Task
  }> =>
    postJSON({
      method: 'POST',
      url: `list/tasks/${task.id}/snooze`,
      json: { note: comment }
    })
  const { mutateAsync, isLoading } = useMutation(mutationFunction, {
    onSuccess: () => {
      showToast({ title: t(toDosConstants.SNOOZED) })
      onSuccess?.()
    }
  })

  const handleSubmit = (formValues: { comment?: string }) => {
    mutateAsync(formValues)
  }

  const [form] = Form.useForm<{ comment?: string }>()

  return (
    <Modal
      closeButton
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      classicPadding
      closeOnConfirm={false}
      width580px
      onConfirm={form.submit}
      onCancel={() => ''}
      localisation={{
        confirmText: t(toDosConstants.SNOOZE),
        cancelText: t('general.buttons.cancel', {
          ns: NAMESPACE.RESOURCES
        })
      }}
      confirmButtonProps={{ variant: 'primary', isLoading }}
    >
      <TitleContainer>
        <Warning size={24} color={theme.color.status.warning} />
        <Title level={3}>{t(toDosConstants.SNOOZE)}</Title>
      </TitleContainer>
      <Divider />
      <ModalDescription text={t(toDosConstants.SNOOZE_DESCRIPTION)} />
      <Form<{ comment?: string }> onFinish={handleSubmit} form={form}>
        <Form.Item
          rules={[
            {
              required: true,
              message: t(generalConstants.REQUIRED_MESSAGE, {
                ns: NAMESPACE.GENERAL
              })
            }
          ]}
          name={ITEM_NAME}
        >
          <CommentSection required />
        </Form.Item>
      </Form>
      <Divider />
    </Modal>
  )
}
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`
const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

export default SnoozeModal
